/**
 * Personalizzazioni per-organizzazione dei materiali di catalogo.
 *
 * Modello "delta": il materiale globale non viene copiato né modificato — l'org
 * salva solo i campi che vuole diversi (prezzo netto, tempo di posa). La view
 * `materials_with_pricing` applica la precedenza (custom > listino × sconto
 * famiglia > listino), quindi configuratore, pareti certificate e preventivi
 * usano automaticamente i valori personalizzati.
 *
 * ATTENZIONE UNITÀ: `custom_installation_time_per_sqm` è in ORE (come
 * `materials.installation_time_per_sqm`), mentre la UI lavora in MINUTI.
 * La conversione (×60 / ÷60) è responsabilità del chiamante, come nel form materiali.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';

export interface MaterialCustomizationInput {
  materialId: string;
  /** Prezzo netto in €/unità. null = rimuovi l'override e torna al prezzo di catalogo. */
  customNetPrice: number | null;
  /** Tempo di posa in ORE. null = rimuovi l'override. */
  customInstallationTimeHours: number | null;
  /** Famiglia scelta dall'org (gli sconti seguono questa). null = usa quella di catalogo. */
  customFamilyId?: string | null;
  /** Sfrido % per questa org. null = usa quello del materiale, poi la categoria. */
  customWastePercentage?: number | null;
  /** Smaltimento % per questa org. null = usa quello del materiale, poi il default org. */
  customDisposalPercentage?: number | null;
  notes?: string | null;
}

/** Invalida tutto ciò che dipende dai prezzi materiali. */
const invalidatePricing = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['materials'] });
  qc.invalidateQueries({ queryKey: ['material-customizations'] });
  // stratigrafie/preventivi ricalcolano dai prezzi live
  qc.invalidateQueries({ queryKey: ['unified-stratigraphies'] });
  qc.invalidateQueries({ queryKey: ['stratigraphies'] });
  // La singola parete aperta nel configuratore ha la sua chiave, al SINGOLARE
  // (`useStratigraphy` → ['stratigraphy', id]): 'stratigraphies' non la tocca.
  // Senza questa riga, cambiare un prezzo o un tempo e tornare sulla parete
  // mostrava ancora i valori di prima, presi dalla cache.
  qc.invalidateQueries({ queryKey: ['stratigraphy'] });
};

/**
 * Salva (upsert) la personalizzazione di un materiale per l'org corrente.
 * Se entrambi i valori sono null, rimuove del tutto la riga (= ripristina catalogo).
 */
export const useSaveMaterialCustomization = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: MaterialCustomizationInput) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione selezionata');

      const noOverrides =
        input.customNetPrice == null &&
        input.customInstallationTimeHours == null &&
        input.customWastePercentage == null &&
        input.customDisposalPercentage == null &&
        !input.customFamilyId;

      if (noOverrides) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('org_material_customizations')
          .delete()
          .eq('organization_id', currentOrganizationId)
          .eq('material_id', input.materialId);
        if (error) throw error;
        return { removed: true };
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('org_material_customizations')
        .upsert(
          {
            organization_id: currentOrganizationId,
            material_id: input.materialId,
            custom_net_price: input.customNetPrice,
            custom_installation_time_per_sqm: input.customInstallationTimeHours,
            custom_family_id: input.customFamilyId ?? null,
            custom_waste_percentage: input.customWastePercentage,
            custom_disposal_percentage: input.customDisposalPercentage,
            notes: input.notes ?? null,
            created_by: user?.id ?? null,
          },
          { onConflict: 'organization_id,material_id' },
        );
      if (error) throw error;
      return { removed: false };
    },
    onSuccess: (res) => {
      invalidatePricing(queryClient);
      toast.success(
        res.removed
          ? 'Ripristinati i valori di catalogo'
          : 'Personalizzazione salvata per la tua azienda',
      );
    },
    onError: (e: unknown) => {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: unknown }).message)
          : String(e);
      toast.error(`Impossibile salvare la personalizzazione: ${msg}`);
    },
  });
};

/** Rimuove la personalizzazione (ripristina i valori di catalogo). */
export const useResetMaterialCustomization = () => {
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (materialId: string) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione selezionata');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('org_material_customizations')
        .delete()
        .eq('organization_id', currentOrganizationId)
        .eq('material_id', materialId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidatePricing(queryClient);
      toast.success('Ripristinati prezzo e tempo di catalogo');
    },
    onError: (e: unknown) => {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: unknown }).message)
          : String(e);
      toast.error(`Impossibile ripristinare: ${msg}`);
    },
  });
};
